import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, Scissors, Layers, Palette, Type, Volume2, 
  Play, Pause, Download, RotateCcw, Zap, Settings,
  Film, Music, Sparkles, MessageSquare, Mic, Home
} from 'lucide-react';
import FileUpload from './FileUpload';
import CommandInterface from './CommandInterface';
import MediaPreview from './MediaPreview';
import ProcessingPanel from './ProcessingPanel';
import EditingTools from './EditingTools';
import { MediaFile, ProcessingCommand } from '../types/media';
import { processCommand } from '../utils/commandParser';
import { AudioProcessor } from '../utils/audioProcessor';
import { VideoProcessor } from '../utils/videoProcessor';

interface EditorWorkspaceProps {
  onBackToHome: () => void;
}

const EditorWorkspace: React.FC<EditorWorkspaceProps> = ({ onBackToHome }) => {
  const [uploadedFile, setUploadedFile] = useState<MediaFile | null>(null);
  const [processedFile, setProcessedFile] = useState<MediaFile | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [commandHistory, setCommandHistory] = useState<ProcessingCommand[]>([]);
  const [currentCommand, setCurrentCommand] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [activeTab, setActiveTab] = useState<'upload' | 'edit' | 'export'>('upload');

  const audioProcessor = useRef(new AudioProcessor());
  const videoProcessor = useRef(new VideoProcessor());

  const handleFileUpload = async (file: File) => {
    const mediaFile: MediaFile = {
      file,
      url: URL.createObjectURL(file),
      type: file.type.startsWith('audio/') ? 'audio' : 'video',
      name: file.name,
      size: file.size,
      duration: 0,
      metadata: {}
    };

    // Get media metadata
    if (mediaFile.type === 'video') {
      const video = document.createElement('video');
      video.src = mediaFile.url;
      video.onloadedmetadata = () => {
        mediaFile.duration = video.duration;
        mediaFile.metadata = {
          width: video.videoWidth,
          height: video.videoHeight
        };
        setUploadedFile({ ...mediaFile });
      };
    } else {
      const audio = document.createElement('audio');
      audio.src = mediaFile.url;
      audio.onloadedmetadata = () => {
        mediaFile.duration = audio.duration;
        setUploadedFile({ ...mediaFile });
      };
    }

    setUploadedFile(mediaFile);
    setProcessedFile(null);
    setCommandHistory([]);
    setActiveTab('edit');
  };

  const handleCommand = async (command: string) => {
    if (!uploadedFile) return;

    setIsProcessing(true);
    setProcessingProgress(0);
    
    const parsedCommand = processCommand(command);
    
    // Simulate processing progress
    const progressInterval = setInterval(() => {
      setProcessingProgress(prev => Math.min(prev + 10, 90));
    }, 200);
    
    try {
      let result: MediaFile;
      
      if (uploadedFile.type === 'audio') {
        result = await audioProcessor.current.processAudio(uploadedFile, parsedCommand);
      } else {
        result = await videoProcessor.current.processVideo(uploadedFile, parsedCommand);
      }
      
      setProcessingProgress(100);
      setTimeout(() => {
        setProcessedFile(result);
        setCommandHistory(prev => [...prev, { ...parsedCommand, status: 'completed' }]);
        clearInterval(progressInterval);
        setIsProcessing(false);
        setProcessingProgress(0);
        setActiveTab('export');
      }, 500);
      
    } catch (error) {
      console.error('Processing failed:', error);
      clearInterval(progressInterval);
      setIsProcessing(false);
      setProcessingProgress(0);
      
      // Show error state
      alert(`Processing failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleToolAction = async (action: string, parameters: any) => {
    if (!uploadedFile) return;
    
    // Convert tool action to command
    let command = '';
    switch (action) {
      case 'cut':
        command = `Cut from ${parameters.startTime} to ${parameters.endTime} seconds`;
        break;
      case 'filter':
        command = `Apply ${parameters.filterType} filter`;
        break;
      case 'speed':
        command = `Change speed to ${parameters.speed}x`;
        break;
      case 'volume':
        command = `Change volume by ${parameters.gain} dB`;
        break;
      case 'pitch':
        command = `Change pitch by ${parameters.semitones} semitones`;
        break;
      default:
        command = action;
    }
    
    await handleCommand(command);
  };

  const handleDownload = () => {
    if (!processedFile) return;
    
    const link = document.createElement('a');
    link.href = processedFile.url;
    link.download = processedFile.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleReset = () => {
    setUploadedFile(null);
    setProcessedFile(null);
    setCommandHistory([]);
    setCurrentCommand('');
    setProcessingProgress(0);
    setActiveTab('upload');
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-gray-800 bg-black sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onBackToHome}
                className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
              >
                <Home className="w-5 h-5" />
                <span>Back to Home</span>
              </motion.button>
              
              <div className="w-px h-6 bg-gray-700"></div>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <Zap className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">AI Media Editor</h1>
                  <p className="text-sm text-gray-400">Professional Editing Workspace</p>
                </div>
              </div>
            </div>
            
            {/* Tab Navigation */}
            <div className="flex items-center space-x-1 bg-gray-900 rounded-lg p-1">
              {[
                { id: 'upload', label: 'Upload', icon: Upload },
                { id: 'edit', label: 'Edit', icon: Scissors },
                { id: 'export', label: 'Export', icon: Download }
              ].map((tab) => (
                <motion.button
                  key={tab.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === tab.id 
                      ? 'bg-white text-black' 
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span className="font-medium">{tab.label}</span>
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        <AnimatePresence mode="wait">
          {activeTab === 'upload' && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="flex items-center justify-center min-h-[70vh]"
            >
              <FileUpload onFileUpload={handleFileUpload} />
            </motion.div>
          )}

          {activeTab === 'edit' && uploadedFile && (
            <motion.div
              key="edit"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 xl:grid-cols-4 gap-6"
            >
              {/* Left Panel - Editing Tools */}
              <div className="xl:col-span-1">
                <EditingTools
                  fileType={uploadedFile.type}
                  onToolAction={handleToolAction}
                  isProcessing={isProcessing}
                />
              </div>

              {/* Center Panel - Media Preview */}
              <div className="xl:col-span-2">
                <MediaPreview
                  originalFile={uploadedFile}
                  processedFile={processedFile}
                  isProcessing={isProcessing}
                  onResetPreview={() => setProcessedFile(null)}
                />
                
                {/* Processing Progress */}
                {isProcessing && (
                  <div className="mt-4 bg-gray-900 rounded-lg p-4 border border-gray-800">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Processing Progress</span>
                      <span className="text-sm text-gray-400">{processingProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-white h-2 rounded-full transition-all duration-300"
                        style={{ width: `${processingProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Panel - AI Commands */}
              <div className="xl:col-span-1">
                <CommandInterface
                  onCommand={handleCommand}
                  currentCommand={currentCommand}
                  setCurrentCommand={setCurrentCommand}
                  isProcessing={isProcessing}
                  commandHistory={commandHistory}
                  fileType={uploadedFile.type}
                />
              </div>
            </motion.div>
          )}

          {activeTab === 'export' && (uploadedFile || processedFile) && (
            <motion.div
              key="export"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 xl:grid-cols-3 gap-6"
            >
              {/* Left Panel - Preview */}
              <div className="xl:col-span-2">
                <MediaPreview
                  originalFile={uploadedFile!}
                  processedFile={processedFile}
                  isProcessing={isProcessing}
                  onResetPreview={() => setProcessedFile(null)}
                />
              </div>

              {/* Right Panel - Export Options */}
              <div className="xl:col-span-1">
                <ProcessingPanel
                  uploadedFile={uploadedFile!}
                  processedFile={processedFile}
                  onDownload={handleDownload}
                  onReset={handleReset}
                  onPreview={() => {}}
                  isProcessing={isProcessing}
                  commandHistory={commandHistory}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Quick Actions Bar */}
        {uploadedFile && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-900 rounded-lg border border-gray-800 p-4 shadow-2xl"
          >
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                {uploadedFile.type === 'video' ? (
                  <Film className="w-4 h-4" />
                ) : (
                  <Music className="w-4 h-4" />
                )}
                <span>{uploadedFile.name}</span>
              </div>
              
              <div className="w-px h-4 bg-gray-700"></div>
              
              <div className="flex items-center space-x-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveTab('edit')}
                  className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded text-sm transition-colors"
                >
                  Edit
                </motion.button>
                
                {processedFile && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleDownload}
                    className="px-3 py-1 bg-white text-black hover:bg-gray-200 rounded text-sm font-medium transition-colors"
                  >
                    Download
                  </motion.button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default EditorWorkspace;